import { useState, useEffect, useCallback } from 'react';
import { useSupplierAuth } from '../../context/SupplierAuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import SupplierOnboarding from './SupplierOnboarding';
import { 
  Inbox, CheckCircle, Clock, ArrowRight, Shield, CreditCard,
  Building2, Eye, FileText, Target, AlertCircle, ChevronRight
} from 'lucide-react';
import { Link } from 'react-router-dom';

const STATUS_MAP = {
  concept: { label: 'Concept', color: 'bg-neutral-100 text-neutral-600', desc: 'Profiel wordt opgebouwd' },
  in_review: { label: 'In review', color: 'bg-blue-100 text-blue-700', desc: 'Profiel wordt beoordeeld door Spooker' },
  aanvulling_gevraagd: { label: 'Aanvulling gevraagd', color: 'bg-amber-100 text-amber-700', desc: 'Spooker heeft feedback gegeven' },
  goedgekeurd: { label: 'Goedgekeurd', color: 'bg-green-100 text-green-700', desc: 'Profiel goedgekeurd, wordt live gezet' },
  live: { label: 'Live', color: 'bg-green-100 text-green-700', desc: 'Zichtbaar voor klanten' },
  gepauzeerd: { label: 'Gepauzeerd', color: 'bg-amber-100 text-amber-700', desc: 'Tijdelijk niet zichtbaar' },
  afgekeurd: { label: 'Afgekeurd', color: 'bg-red-100 text-red-700', desc: 'Niet geschikt voor publicatie' },
};

const SupplierDashboard = () => {
  const { api, organization, user } = useSupplierAuth();
  const [stats, setStats] = useState(null);
  const [completeness, setCompleteness] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [onboardingDismissed, setOnboardingDismissed] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, compRes, profileRes] = await Promise.all([
        api.get('/dashboard/stats').catch(() => ({ data: {} })),
        api.get('/profile/completeness').catch(() => ({ data: { percentage: 0, missing: [] } })),
        api.get('/profile').catch(() => ({ data: { profile: {} } })),
      ]);
      setStats(statsRes.data);
      setCompleteness(compRes.data);
      
      if (!onboardingDismissed) {
        const profile = profileRes.data?.profile;
        if (profile && !profile.onboarding_completed) {
          setShowOnboarding(true);
        }
      }
    } catch (e) {
      console.error('Dashboard fetch error:', e);
    } finally {
      setLoading(false);
    }
  }, [api, onboardingDismissed]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-black"></div>
      </div>
    );
  }

  // Onboarding overlay
  if (showOnboarding) {
    return <SupplierOnboarding onComplete={() => { setOnboardingDismissed(true); setShowOnboarding(false); fetchData(); }} />;
  }

  const profileStatus = organization?.profile_status || 'concept';
  const statusInfo = STATUS_MAP[profileStatus] || STATUS_MAP.concept;
  const pct = completeness?.percentage || organization?.profile_completeness || 0;
  const isLive = profileStatus === 'live';
  const needsProfile = pct < 60;

  // Onboarding steps
  const onboardingSteps = [
    { label: 'Profiel invullen', done: pct >= 60, href: '/leverancier/profiel' },
    { label: 'Indienen voor beoordeling', done: ['in_review', 'goedgekeurd', 'live'].includes(profileStatus), href: '/leverancier/profiel' },
    { label: 'Goedkeuring en live gaan', done: isLive, href: '/leverancier/profiel' },
  ];
  const stepsCompleted = onboardingSteps.filter(s => s.done).length;

  return (
    <div className="space-y-6" data-testid="supplier-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-black" data-testid="supplier-dashboard-title">Dashboard</h1>
          <p className="text-neutral-500 text-sm">Welkom terug, {user?.full_name || 'Leverancier'}</p>
        </div>
        <Badge className={statusInfo.color} data-testid="supplier-profile-status-badge">{statusInfo.label}</Badge>
      </div>

      {/* Profile Status Block (always visible) */}
      <Card className={`border shadow-sm ${isLive ? 'border-green-200 bg-green-50/30' : needsProfile ? 'border-amber-200 bg-amber-50/30' : 'border-neutral-200'}`} data-testid="profile-status-card">
        <CardContent className="p-5">
          <div className="flex items-start gap-4">
            <div className={`h-11 w-11 rounded-xl flex items-center justify-center shrink-0 ${isLive ? 'bg-green-100' : needsProfile ? 'bg-amber-100' : 'bg-blue-100'}`}>
              {isLive ? <Eye className="h-5 w-5 text-green-600" strokeWidth={1.5} /> :
               needsProfile ? <Building2 className="h-5 w-5 text-amber-600" strokeWidth={1.5} /> :
               <Shield className="h-5 w-5 text-blue-600" strokeWidth={1.5} />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-sm font-bold text-black">Profielstatus</span>
                <Badge className={statusInfo.color + ' text-[10px]'}>{statusInfo.label}</Badge>
              </div>
              <p className="text-xs text-neutral-500">{statusInfo.desc}</p>
              <div className="mt-3 flex items-center gap-3">
                <div className="flex-1"><Progress value={pct} className="h-1.5" /></div>
                <span className="text-xs font-bold text-neutral-600 shrink-0">{pct}% compleet</span>
              </div>
            </div>
            <Link to="/leverancier/profiel" className="shrink-0">
              <Button size="sm" variant="outline" data-testid="profile-edit-btn">
                {needsProfile ? 'Profiel opbouwen' : 'Profiel bewerken'}
                <ChevronRight className="h-4 w-4 ml-1" strokeWidth={1.5} />
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>

      {/* Onboarding Progress (show until live) */}
      {!isLive && (
        <Card className="border-neutral-200" data-testid="onboarding-progress-card">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm font-bold text-black">Nog {3 - stepsCompleted} stap{3 - stepsCompleted !== 1 ? 'pen' : ''} tot zichtbaarheid</p>
              <span className="text-xs text-neutral-400">{stepsCompleted}/3 voltooid</span>
            </div>
            <div className="space-y-3">
              {onboardingSteps.map((s, idx) => (
                <Link to={s.href} key={s.label} className="flex items-center gap-3 p-3 rounded-lg bg-neutral-50 hover:bg-neutral-100 transition-colors">
                  {s.done ? (
                    <CheckCircle className="h-5 w-5 text-green-500 shrink-0" strokeWidth={1.5} />
                  ) : (
                    <div className="h-5 w-5 rounded-full border-2 border-neutral-300 shrink-0 flex items-center justify-center">
                      <span className="text-[10px] font-bold text-neutral-400">{idx + 1}</span>
                    </div>
                  )}
                  <span className={`text-sm font-medium ${s.done ? 'text-neutral-400 line-through' : 'text-black'}`}>{s.label}</span>
                  {!s.done && <ChevronRight className="h-4 w-4 text-neutral-300 ml-auto" strokeWidth={1.5} />}
                </Link>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Match Readiness */}
      {isLive && (
        <Card className="border-green-200 bg-green-50/20" data-testid="match-readiness-card">
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-green-100 flex items-center justify-center">
                <Target className="h-5 w-5 text-green-600" strokeWidth={1.5} />
              </div>
              <div className="flex-1">
                <p className="text-sm font-bold text-black">Geschikt voor top 3-selectie</p>
                <p className="text-xs text-neutral-500">Uw profiel is goedgekeurd en telt mee voor matching en klantadvies.</p>
              </div>
              <Badge className="bg-green-100 text-green-700">Actief</Badge>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { to: '/leverancier/leads', icon: Inbox, bg: 'bg-blue-100', val: stats?.leads?.new || 0, label: 'Nieuwe Leads', sub: 'Wachtend op actie' },
          { to: '/leverancier/leads', icon: Clock, bg: 'bg-amber-100', val: (stats?.leads?.counts?.contact_gelegd || 0) + (stats?.leads?.counts?.voorstel_gedaan || 0) + (stats?.leads?.counts?.bezig || 0), label: 'In Behandeling', sub: '' },
          { to: '/leverancier/leads', icon: CheckCircle, bg: 'bg-green-100', val: stats?.leads?.counts?.afgerond || 0, label: 'Afgerond', sub: '' },
          { to: '/leverancier/finance', icon: CreditCard, bg: 'bg-purple-100', val: `€${stats?.finance?.pending?.total?.toLocaleString('nl-NL') || '0'}`, label: 'Openstaand', sub: '' },
        ].map((s) => (
          <Link to={s.to} key={s.label}>
            <Card className="hover:shadow-md hover:border-neutral-300 transition-all cursor-pointer" data-testid={`stat-${s.label.toLowerCase().replace(/\s+/g, '-')}`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-neutral-500">{s.label}</p>
                    <p className="text-2xl font-bold text-black mt-1">{s.val}</p>
                    {s.sub && <p className="text-[10px] text-neutral-400 mt-0.5">{s.sub}</p>}
                  </div>
                  <div className={`h-10 w-10 ${s.bg} rounded-xl flex items-center justify-center`}>
                    <s.icon className="h-5 w-5 text-blue-600" strokeWidth={1.5} />
                  </div>
                </div>
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>

      {/* Recent Leads & Improvement Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Leads */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-base font-bold">Recente Leads</CardTitle>
            <Link to="/leverancier/leads">
              <Button variant="ghost" size="sm" className="text-xs">Alle bekijken <ArrowRight className="h-3.5 w-3.5 ml-1" /></Button>
            </Link>
          </CardHeader>
          <CardContent>
            {stats?.recent_leads?.length > 0 ? (
              <div className="space-y-2">
                {stats.recent_leads.slice(0, 5).map((lead) => (
                  <div key={lead.id} className="flex items-center justify-between p-3 bg-neutral-50 rounded-lg">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-black truncate">{lead.shared_data?.company_name || 'Bedrijf'}</p>
                      <p className="text-xs text-neutral-500 truncate">{lead.help_type}</p>
                    </div>
                    <Badge variant={lead.status === 'nieuw' ? 'default' : 'outline'} className="text-[10px]">{lead.status}</Badge>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <div className="inline-flex items-center justify-center h-14 w-14 rounded-full bg-blue-50 mb-3">
                  <Inbox className="h-7 w-7 text-blue-400" strokeWidth={1.5} />
                </div>
                <p className="text-sm font-medium text-black">Nog geen leads ontvangen</p>
                <p className="text-xs text-neutral-500 mt-1 max-w-[250px] mx-auto">
                  Zodra klanten hulpaanvragen indienen die bij uw expertises passen, verschijnen ze hier.
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Improvement Actions */}
        <Card data-testid="improvement-actions-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-bold">Verbeteracties</CardTitle>
          </CardHeader>
          <CardContent>
            {completeness?.missing?.length > 0 ? (
              <div className="space-y-2">
                {completeness.missing.slice(0, 6).map(m => (
                  <Link to="/leverancier/profiel" key={m.field} className="flex items-center gap-3 p-3 bg-neutral-50 rounded-lg hover:bg-neutral-100 transition-colors">
                    <AlertCircle className="h-4 w-4 text-amber-500 shrink-0" strokeWidth={1.5} />
                    <span className="text-sm text-neutral-700">{m.label} ontbreekt</span>
                    <ChevronRight className="h-4 w-4 text-neutral-300 ml-auto" strokeWidth={1.5} />
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <div className="inline-flex items-center justify-center h-14 w-14 rounded-full bg-green-50 mb-3">
                  <CheckCircle className="h-7 w-7 text-green-400" strokeWidth={1.5} />
                </div>
                <p className="text-sm font-medium text-black">Profiel compleet</p>
                <p className="text-xs text-neutral-500 mt-1">Alle verplichte velden zijn ingevuld.</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SupplierDashboard;
